import { buildOccContract, parseOptionContractDetails } from "./watchOptionContracts";

export type MultiLegStructure = "single" | "vertical" | "iron_condor" | "iron_butterfly";
export type PositionLegSide = "LONG" | "SHORT";
export type StrategyEntryBasisType = "NET_DEBIT" | "NET_CREDIT";

export type MultiLegDraftLeg = {
  side: PositionLegSide;
  optionType: "call" | "put";
  strike: string;
  /** Retained only to hydrate legacy drafts; normal authoring never exposes it. */
  optionContract: string;
  contracts: string;
  entryPremiumPerShare: string;
};

export type MultiLegDraft = {
  structure: MultiLegStructure;
  openedAt: number;
  underlying: string;
  expiry: string;
  legs: MultiLegDraftLeg[];
  strategyEntryBasisType?: StrategyEntryBasisType | "";
  strategyEntryBasisPerShare?: string;
};

const STRUCTURE_CONFIG: Record<MultiLegStructure, { label: string; sides: PositionLegSide[] }> = {
  single: { label: "Single option", sides: ["LONG"] },
  vertical: { label: "Vertical spread", sides: ["LONG", "SHORT"] },
  iron_condor: { label: "Iron condor", sides: ["LONG", "SHORT", "SHORT", "LONG"] },
  iron_butterfly: { label: "Iron butterfly", sides: ["LONG", "SHORT", "SHORT", "LONG"] },
};

export const MULTI_LEG_STRUCTURES = (Object.keys(STRUCTURE_CONFIG) as MultiLegStructure[]).map(
  (id) => ({ id, label: STRUCTURE_CONFIG[id].label }),
).filter((item) => item.id !== "single");

/** Structures available when authoring a held option position. */
export const POSITION_STRUCTURES = (Object.keys(STRUCTURE_CONFIG) as MultiLegStructure[]).map(
  (id) => ({ id, label: STRUCTURE_CONFIG[id].label }),
);

export function makeMultiLegDraft(
  structure: MultiLegStructure = "vertical",
  openedAt = Math.floor(Date.now() / 1000),
): MultiLegDraft {
  return {
    structure,
    openedAt,
    underlying: "",
    expiry: "",
    strategyEntryBasisType: "",
    strategyEntryBasisPerShare: "",
    legs: STRUCTURE_CONFIG[structure].sides.map((side, index) => ({
      side,
      optionType: structure === "vertical" || index < 2 ? "put" : "call",
      strike: "",
      optionContract: "",
      contracts: "1",
      entryPremiumPerShare: "",
    })),
  };
}

type PositionLegSource = {
  option_contract?: unknown;
  side?: unknown;
  contracts?: unknown;
  entry_premium_per_share?: unknown;
};

/**
 * Customer-safe position facts returned by the Watches detail read model.
 * This deliberately has no OCC symbol: the authoring screen reconstructs a
 * canonical contract only after a person has reviewed the readable ticker,
 * expiration, option type, and strike.
 */
export type ReadablePositionLegSource = {
  option?: {
    ticker?: unknown;
    expiry?: unknown;
    strike?: unknown;
    option_type?: unknown;
  };
  side?: unknown;
  contracts?: unknown;
  multiplier?: unknown;
  entry_premium_per_share?: unknown;
  entry_executed_at?: unknown;
};

type ReadableStrategyEntryBasisSource = {
  type?: unknown;
  amount_per_share?: unknown;
};

type ReadablePositionSource = {
  entry_basis?: unknown;
  legs?: unknown;
};

/** Hydrate the friendly authoring form from a stored, canonical position. */
export function makeMultiLegDraftFromPosition(position: unknown): MultiLegDraft | null {
  const basis = typeof position === "object" && position !== null
    ? (position as { position_basis?: { opened_at?: unknown; legs?: unknown } }).position_basis
    : undefined;
  const sourceLegs = Array.isArray(basis?.legs) ? basis.legs as PositionLegSource[] : [];
  if (sourceLegs.length !== 1 && sourceLegs.length !== 2 && sourceLegs.length !== 4) return null;
  const parsedLegs = sourceLegs.map((leg) => parseOptionContractDetails(typeof leg.option_contract === "string" ? leg.option_contract : ""));
  if (parsedLegs.some((leg) => !leg)) return null;
  const parsed = parsedLegs as NonNullable<typeof parsedLegs[number]>[];
  const structure: MultiLegStructure = sourceLegs.length === 1
    ? "single"
    : sourceLegs.length === 2
    ? "vertical"
    : parsed.filter((leg) => leg.type === "put").sort((a, b) => Number(a.strike) - Number(b.strike))[1]?.strike === parsed.filter((leg) => leg.type === "call").sort((a, b) => Number(a.strike) - Number(b.strike))[0]?.strike
      ? "iron_butterfly"
      : "iron_condor";
  const openedAt = Number(basis?.opened_at);
  return {
    structure,
    openedAt: Number.isInteger(openedAt) && openedAt > 0 ? openedAt : Math.floor(Date.now() / 1000),
    underlying: parsed[0].ticker,
    expiry: parsed[0].expiry,
    legs: sourceLegs.map((leg, index) => ({
      side: leg.side === "SHORT" ? "SHORT" : "LONG",
      optionType: parsed[index].type,
      strike: parsed[index].strike,
      optionContract: String(leg.option_contract || ""),
      contracts: String(leg.contracts || "1"),
      entryPremiumPerShare: String(leg.entry_premium_per_share || ""),
    })),
  };
}

/** JS-1081: one Desk-idea priced leg (already-decoded, not an OCC string). */
export type PricedLegSource = {
  contract?: unknown;
  action?: unknown;
  option_type?: unknown;
  strike?: unknown;
  expiry?: unknown;
};

/**
 * Hydrate a "+Watch" pre-fill draft from a Desk idea's own priced legs
 * (``entry.priced_legs`` -- contract/action/option_type/strike/expiry, ALREADY
 * decoded server-side, unlike ``makeMultiLegDraftFromPosition``'s
 * OCC-string-only ``PositionLegSource`` input above). Returns ``null``
 * (honest-absence, never a guessed structure) for any shape this authoring
 * screen does not support: not exactly 1/2/4 legs, an unrecognized
 * ``option_type``, or an unrecognized ``action``.
 *
 * ``entryFillPricePerShare`` is the idea's own displayed NET structure fill,
 * SIGNED (positive = net debit paid, negative = net credit received --
 * never mid; the same convention `derivePositionBasisPerShare`/
 * `domains/watches/lib/exitRuleWatchTriggers.ts` use). It is distributed to a
 * leg's ``entryPremiumPerShare`` directly ONLY when the structure is a single
 * option (exactly one leg, so the net price IS that leg's price, rendered as
 * its POSITIVE magnitude -- `MultiLegDraftLeg.entryPremiumPerShare` is always
 * a price paid/received, never signed).
 *
 * For a MULTI-leg structure, ``perLegRealisticFillByContract`` (JS-1081
 * adversarial-review FIX 1 correction) supplies each leg's OWN real
 * execution-friction fill (``execution_friction.legs[].realistic_fill``,
 * keyed by OCC contract) -- this is REAL per-leg pricing the backend already
 * computed, never a fabricated split of the net price. When every leg's
 * contract resolves a fill in that map, each leg's ``entryPremiumPerShare``
 * is populated with its own magnitude; the wizard's pre-filled authoring
 * screen (``isPosition && initialState``) shows PER-LEG premium inputs, not
 * the shared whole-structure basis field, so populating real per-leg values
 * is what actually lets `derivePositionBasisPerShare` resolve a basis for
 * this screen.
 *
 * JS-1081 (round 3 adversarial-review FIX 2): a multi-leg structure with an
 * INCOMPLETE per-leg fill map is an honest failure (``null``), never a
 * whole-structure ``strategyEntryBasisType``/``strategyEntryBasisPerShare``
 * fallback. That fallback previously populated a basis the wizard's
 * pre-filled multi-leg screen never actually RENDERS (it shows per-leg
 * premium inputs, not the shared basis field) -- a hidden, authoritative
 * value sitting behind visible-but-blank editable fields, which a customer
 * could submit without ever seeing what price it priced against. Requiring
 * a complete per-leg map keeps the ONLY value this draft can ever resolve a
 * basis from (see `derivePositionBasisPerShare`) the same one the rendered
 * per-leg inputs show and let the customer edit.
 */
export function makeMultiLegDraftFromPricedLegs(
  legs: PricedLegSource[],
  entryFillPricePerShare: string,
  perLegRealisticFillByContract?: Readonly<Record<string, string>>,
): MultiLegDraft | null {
  if (legs.length !== 1 && legs.length !== 2 && legs.length !== 4) return null;
  // Verified against the actual backend payload (services/apis/domains/
  // trade_ideas/identity.py's `OptionRight` enum, which every
  // `PricedEntryLeg.option_type` value derives from): upper-case
  // "CALL"/"PUT", never lower-case -- this is NOT the same casing as
  // `MultiLegDraftLeg.optionType` ("call"/"put"), which is why this
  // function maps explicitly below rather than assigning verbatim.
  if (legs.some((leg) => leg.option_type !== "CALL" && leg.option_type !== "PUT")) return null;
  if (legs.some((leg) => leg.action !== "BUY" && leg.action !== "SELL")) return null;
  if (legs.some((leg) => typeof leg.strike !== "string" || typeof leg.expiry !== "string")) return null;
  if (!/^-?\d+(?:\.\d{1,4})?$/.test(entryFillPricePerShare.trim())) return null;
  const fillMagnitude = entryFillPricePerShare.trim().replace(/^-/, "");
  if (!(Number(fillMagnitude) > 0)) return null;

  const rawTyped = legs as Array<{
    contract?: unknown;
    action: "BUY" | "SELL";
    option_type: "CALL" | "PUT";
    strike: string;
    expiry: string;
  }>;
  const optionType = (leg: (typeof rawTyped)[number]): "call" | "put" => (leg.option_type === "CALL" ? "call" : "put");

  const structure: MultiLegStructure = rawTyped.length === 1
    ? "single"
    : rawTyped.length === 2
    ? "vertical"
    : rawTyped.filter((leg) => leg.option_type === "PUT").sort((a, b) => Number(a.strike) - Number(b.strike))[1]?.strike
        === rawTyped.filter((leg) => leg.option_type === "CALL").sort((a, b) => Number(a.strike) - Number(b.strike))[0]?.strike
      ? "iron_butterfly"
      : "iron_condor";

  // JS-1081 (adversarial-review FIX 5): a 2-leg vertical's positional order
  // is a CONTRACT the rest of this authoring screen relies on --
  // `STRUCTURE_CONFIG.vertical.sides` (this same module) is `["LONG",
  // "SHORT"]`, and `legRoleLabel` (WatchCreateWizard.tsx) labels index 0
  // "Long leg" / index 1 "Short leg" unconditionally. The backend does NOT
  // guarantee this order for every archetype: verified against
  // `services/apis/domains/trade_ideas/shadow_option_expressions.py`'s
  // `enumerate_shadow_option_expressions` -- BULL_PUT_CREDIT legs are
  // constructed `(BUY, SELL)` (already LONG-first), but BEAR_CALL_CREDIT is
  // constructed `(SELL, BUY)` (SHORT-first) -- the exact opposite. Sorting
  // by action (BUY before SELL) canonicalizes EVERY 2-leg input into the
  // order this screen requires, regardless of which archetype or
  // construction path produced it, rather than trusting input order. A
  // condor's 4-leg order is a SEPARATE, already-correct contract
  // (`[LONG, SHORT, SHORT, LONG]`, verified against the SAME module's
  // `(long_put BUY, short_put SELL, short_call SELL, long_call BUY)`
  // condor construction) and is left untouched here.
  const typed = structure === "vertical"
    ? [...rawTyped].sort((a, b) => (a.action === b.action ? 0 : a.action === "BUY" ? -1 : 1))
    : rawTyped;

  const ticker = typeof legs[0].contract === "string"
    ? parseOptionContractDetails(legs[0].contract)?.ticker ?? ""
    : "";
  const isMultiLeg = typed.length > 1;
  const perLegFills = typed.map((leg) => {
    const contract = typeof leg.contract === "string" ? leg.contract : "";
    const fill = perLegRealisticFillByContract?.[contract]?.trim();
    return fill && /^-?\d+(?:\.\d{1,4})?$/.test(fill) ? fill.replace(/^-/, "") : null;
  });
  const hasCompletePerLegFills = perLegFills.every((fill) => fill !== null && Number(fill) > 0);
  // JS-1081 (round 3 adversarial-review FIX 2): a multi-leg structure without
  // a complete per-leg fill for EVERY leg is an honest failure -- never a
  // whole-structure basis fallback hidden behind the per-leg inputs the
  // wizard actually renders for this draft. See the function docstring.
  if (isMultiLeg && !hasCompletePerLegFills) return null;

  return {
    structure,
    openedAt: Math.floor(Date.now() / 1000),
    underlying: ticker,
    expiry: typed[0].expiry,
    legs: typed.map((leg, index) => ({
      side: leg.action === "BUY" ? "LONG" : "SHORT",
      optionType: optionType(leg),
      strike: leg.strike,
      optionContract: typeof leg.contract === "string" ? leg.contract : "",
      contracts: "1",
      // See the function docstring: for a single leg, the net structure
      // price IS that leg's price. For multi-leg, a REAL per-leg fill is
      // guaranteed complete by the early-return above. Always the positive
      // magnitude -- never the signed input.
      entryPremiumPerShare: !isMultiLeg ? fillMagnitude : perLegFills[index]!,
    })),
  };
}

// ---------------------------------------------------------------------------
// Exact fixed-point decimal helpers (BigInt-backed, no floating point).
// Deliberately self-contained rather than shared with
// domains/trade-ideas/lib/exitRuleWatchMapping.ts's identical helpers: that
// module lives in a different domain, and duplicating ~20 lines of pure
// arithmetic is cheaper than adding a cross-domain import for it.
// ---------------------------------------------------------------------------

function parseExactDecimal(value: string): { negative: boolean; unscaled: bigint; scale: number } {
  const trimmed = value.trim();
  const negative = trimmed.startsWith("-");
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole, fraction = ""] = unsigned.split(".");
  const scale = fraction.length;
  const unscaled = BigInt((whole || "0") + fraction || "0");
  return { negative, unscaled, scale };
}

function renderExactDecimal(unscaled: bigint, scale: number): string {
  const negative = unscaled < 0n;
  let digits = (negative ? -unscaled : unscaled).toString();
  if (scale === 0) return `${negative && unscaled !== 0n ? "-" : ""}${digits}`;
  digits = digits.padStart(scale + 1, "0");
  const whole = digits.slice(0, digits.length - scale);
  const fraction = digits.slice(digits.length - scale).replace(/0+$/, "");
  const rendered = fraction ? `${whole}.${fraction}` : whole;
  return negative && unscaled !== 0n ? `-${rendered}` : rendered;
}

function addExactDecimal(a: string, b: string): string {
  const left = parseExactDecimal(a);
  const right = parseExactDecimal(b);
  const scale = Math.max(left.scale, right.scale);
  const leftScaled = (left.negative ? -1n : 1n) * left.unscaled * 10n ** BigInt(scale - left.scale);
  const rightScaled = (right.negative ? -1n : 1n) * right.unscaled * 10n ** BigInt(scale - right.scale);
  return renderExactDecimal(leftScaled + rightScaled, scale);
}

function negateExactDecimal(value: string): string {
  const parsed = parseExactDecimal(value);
  if (parsed.unscaled === 0n) return renderExactDecimal(0n, parsed.scale);
  return renderExactDecimal(parsed.negative ? parsed.unscaled : -parsed.unscaled, parsed.scale);
}

function isPositiveExactDecimal(value: string): boolean {
  const parsed = parseExactDecimal(value);
  return !parsed.negative && parsed.unscaled > 0n;
}

/**
 * JS-1081 (adversarial review FIX 1): the LIVE, per-share signed entry basis
 * (positive = net debit paid, negative = net credit received -- the SAME
 * sign convention `domains/trade-ideas/lib/exitRuleWatchMapping.ts`'s
 * `fillPricePerShare` parameter uses) plus the CONFIRMED quantity, derived
 * fresh from `draft`'s CURRENT state every time it is called.
 *
 * This mirrors `positionEntryBasisSummary` (in WatchCreateWizard.tsx)'s
 * signed-per-share derivation exactly, but returns exact decimal STRINGS
 * (never a floating-point number) since the result feeds a financial
 * threshold computation, not just a rendered display string. Returns `null`
 * when the basis or quantity is not yet a valid positive value -- honest
 * absence, never a guessed default.
 */
export function derivePositionBasisPerShare(
  draft: MultiLegDraft,
): { signedPerShare: string; quantity: string } | null {
  const quantityNumber = Number(draft.legs[0]?.contracts);
  if (!Number.isInteger(quantityNumber) || quantityNumber <= 0) return null;
  const quantity = String(quantityNumber);

  if (draft.strategyEntryBasisType && draft.strategyEntryBasisPerShare) {
    const amountPerShare = draft.strategyEntryBasisPerShare.trim();
    if (!/^\d+(?:\.\d{1,4})?$/.test(amountPerShare) || !isPositiveExactDecimal(amountPerShare)) return null;
    return {
      signedPerShare: draft.strategyEntryBasisType === "NET_CREDIT"
        ? negateExactDecimal(amountPerShare)
        : amountPerShare,
      quantity,
    };
  }

  const premiums = draft.legs.map((leg) => leg.entryPremiumPerShare?.trim() ?? "");
  if (premiums.some((premium) => !/^\d+(?:\.\d{1,4})?$/.test(premium) || !isPositiveExactDecimal(premium))) {
    return null;
  }
  // Positive = net debit paid: a LONG leg's premium PAID adds to cost; a
  // SHORT leg's premium RECEIVED offsets it. This is the OPPOSITE sign
  // convention from positionEntryBasisSummary's own `signedCreditPerShare`
  // (which is positive = credit) -- do not copy that function's sign here.
  const signedPerShare = draft.legs.reduce(
    (total, leg, index) => addExactDecimal(
      total,
      leg.side === "LONG" ? premiums[index] : negateExactDecimal(premiums[index]),
    ),
    "0",
  );
  return { signedPerShare, quantity };
}

/** Hydrate a duplicate/replace draft from the public detail read model. */
export function makeMultiLegDraftFromReadablePosition(positionValue: unknown): MultiLegDraft | null {
  // The array form is the pre-v2 call shape. Keep accepting it so legacy
  // per-leg-fill Watches and any bookmarked authoring route remain usable.
  const position: ReadablePositionSource = Array.isArray(positionValue)
    ? { legs: positionValue }
    : typeof positionValue === "object" && positionValue !== null
      ? positionValue as ReadablePositionSource
      : {};
  const legsValue = position.legs;
  if (!Array.isArray(legsValue) || (legsValue.length !== 1 && legsValue.length !== 2 && legsValue.length !== 4)) return null;
  const legs = legsValue as ReadablePositionLegSource[];
  const first = legs[0]?.option;
  const ticker = typeof first?.ticker === "string" ? first.ticker.trim().toUpperCase() : "";
  const expiry = typeof first?.expiry === "string" ? first.expiry.trim() : "";
  if (!/^[A-Z]{1,10}$/.test(ticker) || !/^\d{4}-\d{2}-\d{2}$/.test(expiry)) return null;

  const normalized = legs.map((leg) => {
    const option = leg.option;
    const optionTicker = typeof option?.ticker === "string" ? option.ticker.trim().toUpperCase() : "";
    const optionExpiry = typeof option?.expiry === "string" ? option.expiry.trim() : "";
    const optionType = String(option?.option_type || "").toLowerCase();
    const strike = option?.strike == null ? "" : String(option.strike).trim();
    const contracts = Number(leg.contracts);
    const hasMultiplier = leg.multiplier !== null && leg.multiplier !== undefined && String(leg.multiplier).trim() !== "";
    const multiplier = hasMultiplier ? Number(leg.multiplier) : null;
    const hasEntryPremium = leg.entry_premium_per_share !== null && leg.entry_premium_per_share !== undefined && String(leg.entry_premium_per_share).trim() !== "";
    const entryPremiumPerShare = hasEntryPremium ? String(leg.entry_premium_per_share).trim() : "";
    const hasOpenedAt = leg.entry_executed_at !== null && leg.entry_executed_at !== undefined && String(leg.entry_executed_at).trim() !== "";
    const openedAt = hasOpenedAt ? Number(leg.entry_executed_at) : null;
    if (
      optionTicker !== ticker || optionExpiry !== expiry ||
      (optionType !== "call" && optionType !== "put") ||
      !/^\d+(?:\.\d{1,3})?$/.test(strike) || Number(strike) <= 0 ||
      (leg.side !== "LONG" && leg.side !== "SHORT") ||
      !Number.isInteger(contracts) || contracts <= 0 ||
      (hasMultiplier && (!Number.isInteger(multiplier) || multiplier !== 100)) ||
      (hasEntryPremium && (!/^\d+(?:\.\d{1,4})?$/.test(entryPremiumPerShare) || Number(entryPremiumPerShare) <= 0)) ||
      (hasOpenedAt && (!Number.isInteger(openedAt) || openedAt <= 0))
    ) return null;
    return {
      side: leg.side,
      optionType,
      strike,
      optionContract: "",
      contracts: String(contracts),
      multiplier,
      entryPremiumPerShare,
      openedAt,
    } as const;
  });
  if (normalized.some((leg) => leg === null)) return null;
  const readableLegs = normalized as Array<NonNullable<typeof normalized[number]>>;
  if (new Set(readableLegs.map((leg) => leg.contracts)).size !== 1) return null;
  const multipliers = readableLegs.map((leg) => leg.multiplier);
  if (multipliers.some((multiplier) => multiplier !== null) && multipliers.some((multiplier) => multiplier !== 100)) return null;

  const openedAtValues = readableLegs
    .map((leg) => leg.openedAt)
    .filter((value): value is number => value !== null);
  if (new Set(openedAtValues).size > 1) return null;

  let structure: MultiLegStructure;
  let orderedLegs: typeof readableLegs;
  if (readableLegs.length === 1) {
    structure = "single";
    orderedLegs = readableLegs;
  } else if (readableLegs.length === 2) {
    if (
      readableLegs[0].optionType !== readableLegs[1].optionType ||
      readableLegs[0].strike === readableLegs[1].strike ||
      readableLegs[0].side === readableLegs[1].side
    ) return null;
    structure = "vertical";
    orderedLegs = [...readableLegs].sort((a, b) => a.side === b.side ? 0 : a.side === "LONG" ? -1 : 1);
  } else {
    const putLegs = readableLegs.filter((leg) => leg.optionType === "put").sort((a, b) => Number(a.strike) - Number(b.strike));
    const callLegs = readableLegs.filter((leg) => leg.optionType === "call").sort((a, b) => Number(a.strike) - Number(b.strike));
    if (
      putLegs.length !== 2 || callLegs.length !== 2 ||
      putLegs[0].side !== "LONG" || putLegs[1].side !== "SHORT" ||
      callLegs[0].side !== "SHORT" || callLegs[1].side !== "LONG"
    ) return null;
    if (putLegs[1].strike === callLegs[0].strike) {
      structure = "iron_butterfly";
    } else if (Number(putLegs[1].strike) < Number(callLegs[0].strike)) {
      structure = "iron_condor";
    } else {
      return null;
    }
    orderedLegs = [...putLegs, ...callLegs];
  }

  const entryBasisValue = position.entry_basis;
  const hasStrategyEntryBasis = entryBasisValue !== null && entryBasisValue !== undefined;
  let strategyEntryBasisType: StrategyEntryBasisType | "" = "";
  let strategyEntryBasisPerShare = "";
  if (hasStrategyEntryBasis) {
    if (typeof entryBasisValue !== "object") return null;
    const entryBasis = entryBasisValue as ReadableStrategyEntryBasisSource;
    const type = entryBasis.type;
    const amountPerShare = String(entryBasis.amount_per_share ?? "").trim();
    if (
      (type !== "NET_DEBIT" && type !== "NET_CREDIT") ||
      !/^\d+(?:\.\d{1,4})?$/.test(amountPerShare) || Number(amountPerShare) <= 0
    ) return null;
    if (structure === "single") {
      const expectedType = orderedLegs[0].side === "LONG" ? "NET_DEBIT" : "NET_CREDIT";
      if (type !== expectedType) return null;
    }
    const suppliedPremiums = readableLegs.filter((leg) => leg.entryPremiumPerShare !== "");
    if (suppliedPremiums.length !== 0 && suppliedPremiums.length !== readableLegs.length) return null;
    strategyEntryBasisType = type;
    strategyEntryBasisPerShare = amountPerShare;
  } else if (readableLegs.some((leg) => leg.entryPremiumPerShare === "")) {
    // A v1 position is defined by every immutable per-leg fill. Never invent
    // missing fills merely to make a duplicate/replace form render.
    return null;
  }

  const openedAt = openedAtValues[0];
  return {
    structure,
    openedAt: Number.isInteger(openedAt) && openedAt > 0 ? openedAt : Math.floor(Date.now() / 1000),
    underlying: ticker,
    expiry,
    strategyEntryBasisType,
    strategyEntryBasisPerShare,
    legs: orderedLegs.map((leg) => ({
      side: leg.side,
      optionType: leg.optionType,
      strike: leg.strike,
      optionContract: leg.optionContract,
      contracts: leg.contracts,
      entryPremiumPerShare: leg.entryPremiumPerShare,
    })),
  };
}

/** Local datetime-local control value for the immutable position entry time. */
export function formatPositionOpenedAt(openedAt: number) {
  const date = new Date(openedAt * 1000);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function parsePositionOpenedAt(value: string) {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) throw new Error("Enter a valid position open date and time");
  const openedAt = Math.floor(timestamp / 1000);
  if (openedAt > Math.floor(Date.now() / 1000) + 60) {
    throw new Error("Position open time cannot be in the future");
  }
  return openedAt;
}

function decimalString(value: string, label: string) {
  const normalized = value.trim();
  if (!/^\d+(?:\.\d{1,4})?$/.test(normalized) || Number(normalized) <= 0) {
    throw new Error(`${label} must be a positive amount with up to four decimals`);
  }
  return normalized;
}

function parseLeg(draft: MultiLegDraft, leg: MultiLegDraftLeg, index: number) {
  const ticker = (draft.underlying || "").trim().toUpperCase();
  const expiry = (draft.expiry || "").trim();
  const strike = (leg.strike || "").trim();
  const usesLegacyContract = !ticker && !expiry && !strike;
  if (!usesLegacyContract && !/^[A-Z]{1,10}$/.test(ticker)) throw new Error("Enter a valid underlying ticker");
  if (!usesLegacyContract && (!/^\d{4}-\d{2}-\d{2}$/.test(expiry) || Number.isNaN(new Date(`${expiry}T00:00:00`).getTime()))) {
    throw new Error("Enter a valid expiration date");
  }
  if (!usesLegacyContract && (!/^\d+(?:\.\d{1,3})?$/.test(strike) || Number(strike) <= 0)) {
    throw new Error(`Leg ${index + 1} strike must be a positive amount with up to three decimals`);
  }
  const optionContract = usesLegacyContract
    ? leg.optionContract.trim().toUpperCase()
    : buildOccContract({ ticker, contractExpiry: expiry, contractStrike: strike, contractType: leg.optionType });
  const parsed = parseOptionContractDetails(optionContract);
  if (!parsed) throw new Error(`Leg ${index + 1} needs valid contract details`);
  const expiryDate = new Date(`${parsed.expiry}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (expiryDate.getTime() < today.getTime()) {
    throw new Error(`Leg ${index + 1} contract has expired`);
  }
  if (!Number.isInteger(Number(leg.contracts)) || Number(leg.contracts) <= 0) {
    throw new Error(`Leg ${index + 1} contract count must be a positive whole number`);
  }
  return { optionContract, parsed, contracts: Number(leg.contracts) };
}

/** Locate the field that breaks the authored four-leg strike ordering. */
export function getFourLegStrikeOrderIssue(draft: MultiLegDraft): { legIndex: number; message: string } | null {
  if (draft.structure !== "iron_condor" && draft.structure !== "iron_butterfly") return null;
  if (draft.legs.length !== 4) return null;
  const roleIndex = (side: "LONG" | "SHORT", optionType: "put" | "call") => {
    const matches = draft.legs.flatMap((leg, index) => leg.side === side && leg.optionType === optionType ? [index] : []);
    return matches.length === 1 ? matches[0] : -1;
  };
  const [longPutIndex, shortPutIndex, shortCallIndex, longCallIndex] = [
    roleIndex("LONG", "put"), roleIndex("SHORT", "put"), roleIndex("SHORT", "call"), roleIndex("LONG", "call"),
  ];
  if ([longPutIndex, shortPutIndex, shortCallIndex, longCallIndex].includes(-1)) return null;
  const strikes = draft.legs.map((leg) => Number(leg.strike));
  if (strikes.some((strike) => !Number.isFinite(strike) || strike <= 0)) return null;
  const [longPut, shortPut, shortCall, longCall] = [longPutIndex, shortPutIndex, shortCallIndex, longCallIndex].map((index) => strikes[index]);
  if (longPut >= shortPut) return { legIndex: longPutIndex, message: "The long put wing strike must be below the short put strike." };
  if (draft.structure === "iron_butterfly" && shortPut !== shortCall) {
    return { legIndex: shortCallIndex, message: "The short call strike must match the short put strike at the butterfly's center." };
  }
  if (draft.structure === "iron_condor" && shortPut >= shortCall) {
    return { legIndex: shortCallIndex, message: "The short call strike must be above the short put strike for an iron condor." };
  }
  if (shortCall >= longCall) return { legIndex: longCallIndex, message: "The long call wing strike must be above the short call strike." };
  return null;
}

function assertTopology(draft: MultiLegDraft, parsedLegs: ReturnType<typeof parseLeg>[]) {
  const expiries = new Set(parsedLegs.map((leg) => leg.parsed.expiry));
  const tickers = new Set(parsedLegs.map((leg) => leg.parsed.ticker));
  const quantities = new Set(parsedLegs.map((leg) => leg.contracts));
  if (expiries.size !== 1) throw new Error("All legs must use the same expiration");
  if (tickers.size !== 1) throw new Error("All legs must use the same underlying");
  if (quantities.size !== 1) throw new Error("All legs must use the same contract quantity");

  if (draft.structure === "single") {
    if (parsedLegs.length !== 1 || (draft.legs[0]?.side !== "LONG" && draft.legs[0]?.side !== "SHORT")) {
      throw new Error("A single option position requires one long or short option");
    }
    return;
  }

  const byType = (type: "call" | "put") => parsedLegs
    .map((leg, index) => ({ ...leg, side: draft.legs[index].side }))
    .filter((leg) => leg.parsed.type === type)
    .sort((a, b) => Number(a.parsed.strike) - Number(b.parsed.strike));

  if (draft.structure === "vertical") {
    if (parsedLegs.length !== 2 || parsedLegs[0].parsed.type !== parsedLegs[1].parsed.type) {
      throw new Error("A vertical spread requires two call legs or two put legs");
    }
    if (parsedLegs[0].parsed.strike === parsedLegs[1].parsed.strike || draft.legs[0].side === draft.legs[1].side) {
      throw new Error("A vertical spread requires opposing sides at distinct strikes");
    }
    return;
  }

  const strikeOrderIssue = getFourLegStrikeOrderIssue({
    ...draft,
    legs: draft.legs.map((leg, index) => ({ ...leg, strike: parsedLegs[index].parsed.strike, optionType: parsedLegs[index].parsed.type })),
  });
  if (strikeOrderIssue) throw new Error(strikeOrderIssue.message);
  const puts = byType("put");
  const calls = byType("call");
  if (parsedLegs.length !== 4 || puts.length !== 2 || calls.length !== 2) {
    throw new Error("This structure requires two put legs and two call legs");
  }
  if (puts[0].side !== "LONG" || puts[1].side !== "SHORT" || calls[0].side !== "SHORT" || calls[1].side !== "LONG") {
    throw new Error("Use long wings and short inner legs for this structure");
  }
  if (draft.structure === "iron_butterfly" && puts[1].parsed.strike !== calls[0].parsed.strike) {
    throw new Error("An iron butterfly requires matching short strikes");
  }
  if (draft.structure === "iron_condor" && !(Number(puts[1].parsed.strike) < Number(calls[0].parsed.strike))) {
    throw new Error("An iron condor requires distinct short put and short call strikes");
  }
}

/** Build the exact normalized position accepted by the Watch API. */
export function buildMultiLegPosition(draft: MultiLegDraft) {
  if (!Number.isInteger(draft.openedAt) || draft.openedAt <= 0) {
    throw new Error("Position open time is required");
  }
  if (draft.openedAt > Math.floor(Date.now() / 1000) + 60) {
    throw new Error("Position open time cannot be in the future");
  }
  const expectedSides = STRUCTURE_CONFIG[draft.structure].sides;
  if (draft.structure !== "single" && (draft.legs.length !== expectedSides.length || draft.legs.some((leg, index) => leg.side !== expectedSides[index]))) {
    throw new Error(`${STRUCTURE_CONFIG[draft.structure].label} legs have an invalid side layout`);
  }
  const parsedLegs = draft.legs.map((leg, index) => parseLeg(draft, leg, index));
  assertTopology(draft, parsedLegs);
  if (draft.strategyEntryBasisType && draft.strategyEntryBasisPerShare) {
    return {
      schema_version: 2,
      fees_policy: "fees_excluded_v1",
      position_basis: {
        schema_version: 2,
        currency: "USD",
        opened_at: draft.openedAt,
        valuation_convention: "liquidation_bid_ask_v1",
        contracts: parsedLegs[0].contracts,
        multiplier: 100,
        entry_basis: {
          type: draft.strategyEntryBasisType,
          amount_per_share: decimalString(draft.strategyEntryBasisPerShare, "Strategy entry basis"),
        },
        legs: draft.legs.map((leg, index) => ({
          option_contract: parsedLegs[index].optionContract,
          side: leg.side,
          contract_reference: {
            provider: "user_entered_occ",
            provider_contract_id: parsedLegs[index].optionContract,
            retrieved_at: draft.openedAt,
            shares_per_contract: 100,
          },
        })),
      },
    };
  }
  return {
    schema_version: 1,
    fees_policy: "fees_excluded_v1",
    position_basis: {
      schema_version: 1,
      currency: "USD",
      opened_at: draft.openedAt,
      valuation_convention: "liquidation_bid_ask_v1",
      legs: draft.legs.map((leg, index) => ({
        option_contract: parsedLegs[index].optionContract,
        side: leg.side,
        contracts: parsedLegs[index].contracts,
        multiplier: 100,
        entry_premium_per_share: decimalString(leg.entryPremiumPerShare, `Leg ${index + 1} entry premium`),
        entry_executed_at: draft.openedAt,
        contract_reference: {
          // The backend resolves current quote evidence independently. This is
          // immutable entry-contract metadata for standard OCC contracts.
          provider: "user_entered_occ",
          provider_contract_id: parsedLegs[index].optionContract,
          retrieved_at: draft.openedAt,
          shares_per_contract: 100,
        },
      })),
    },
  };
}

/**
 * Build real, human-authored facts for a prospective structure entry.
 *
 * Unlike a held position, an entry candidate has no OCC identifiers, executed
 * premium, provider metadata, or execution timestamp. The server resolves
 * contracts and opening quotes independently from this bounded leg shape.
 */
export function buildMultiLegCandidate(draft: MultiLegDraft) {
  const expectedSides = STRUCTURE_CONFIG[draft.structure].sides;
  if (draft.legs.length !== expectedSides.length || draft.legs.some((leg, index) => leg.side !== expectedSides[index])) {
    throw new Error(`${STRUCTURE_CONFIG[draft.structure].label} legs have an invalid side layout`);
  }
  const parsedLegs = draft.legs.map((leg, index) => parseLeg(draft, leg, index));
  assertTopology(draft, parsedLegs);

  const expiryDate = new Date(`${parsedLegs[0].parsed.expiry}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (expiryDate.getTime() <= today.getTime()) {
    throw new Error("An entry candidate must expire after today");
  }

  return {
    ticker: draft.underlying.trim().toUpperCase(),
    expiry: parsedLegs[0].parsed.expiry,
    legs: draft.legs.map((leg, index) => ({
      side: leg.side,
      option_type: leg.optionType,
      strike: parsedLegs[index].parsed.strike,
      contracts: parsedLegs[index].contracts,
      multiplier: 100,
    })),
  };
}
